const WEBSITE_DISPLAY = 'www.sentinellegal.co.uk'
const WEBSITE_HREF = 'https://www.sentinellegal.co.uk'
const ADDRESS_LINE = 'Harley House, 29 Cambray Place, Cheltenham GL50 1JN'
const SRA_NO = '811792'

export type StaffEmailSignatureTheme = 'light' | 'dark' | 'minimal'

export interface StaffEmailSignatureFields {
    fullName: string
    jobTitle: string
    email: string
    theme?: StaffEmailSignatureTheme
}

const PANEL_THEMES = {
    light: {
        panelBg: '#f8f6f2',
        panelBorder: '#ddd6cb',
        nameColor: '#1a1a1a',
        titleColor: '#9c6537',
        labelColor: '#b07a4c',
        bodyColor: '#1a1a1a',
        wordmarkColor: '#1a1a1a',
        footerBg: '#efebe4',
        footerText: '#4a4a4a',
        noticeText: '#7a7771',
    },
    dark: {
        panelBg: '#1a1a1a',
        panelBorder: '#b5b5b5',
        nameColor: '#ffffff',
        titleColor: '#c78e5f',
        labelColor: '#c78e5f',
        bodyColor: '#ffffff',
        wordmarkColor: '#ffffff',
        footerBg: '#262626',
        footerText: '#b8b5ae',
        noticeText: '#9a9893',
    },
} as const

export const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

/** Suggest lewis@sentinellegal.co.uk from "Lewis Hoy". */
export const suggestEmailFromName = (fullName: string) => {
    const first = fullName.trim().split(/\s+/)[0] || ''
    const local = first.toLowerCase().replace(/[^a-z0-9]/g, '')
    return local ? `${local}@sentinellegal.co.uk` : ''
}

const personFields = (fullName: string, jobTitle: string, email: string) => {
    const name = escapeHtml(fullName.trim() || 'Your Name')
    const title = escapeHtml(jobTitle.trim() || 'Your Title')
    const mailRaw = email.trim() || 'you@sentinellegal.co.uk'
    return {
        name,
        title,
        mail: escapeHtml(mailRaw),
        mailHref: escapeHtml(mailRaw),
    }
}

const buildMinimalSignatureHtml = (fullName: string, jobTitle: string, email: string) => {
    const { name, title, mail, mailHref } = personFields(fullName, jobTitle, email)

    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-family:Helvetica,Arial,sans-serif;color:#1a1a1a;">
  <tr>
    <td style="width:3px;background-color:#c78e5f;font-size:0;line-height:0;" width="3">&nbsp;</td>
    <td style="width:14px;font-size:0;line-height:0;" width="14">&nbsp;</td>
    <td style="vertical-align:top;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:16px;font-weight:700;color:#1a1a1a;line-height:20px;mso-line-height-rule:exactly;">
            ${name}
          </td>
        </tr>
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:400;color:#9c6537;line-height:16px;mso-line-height-rule:exactly;padding:2px 0 12px 0;">
            ${title}
          </td>
        </tr>
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:12px;font-weight:700;color:#1a1a1a;letter-spacing:0.4px;line-height:16px;mso-line-height-rule:exactly;padding:0 0 10px 0;text-transform:uppercase;">
            Sentinel Legal
          </td>
        </tr>
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:18px;mso-line-height-rule:exactly;">
            <a href="mailto:${mailHref}" style="color:#1a1a1a;text-decoration:none;">${mail}</a>
          </td>
        </tr>
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:18px;mso-line-height-rule:exactly;padding:0 0 10px 0;">
            <a href="${WEBSITE_HREF}" style="color:#1a1a1a;text-decoration:none;">${WEBSITE_DISPLAY}</a>
          </td>
        </tr>
        <tr>
          <td style="font-family:Helvetica,Arial,sans-serif;font-size:11px;color:#7a7771;line-height:15px;mso-line-height-rule:exactly;">
            ${ADDRESS_LINE} · SRA No. ${SRA_NO}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`
}

const buildPanelSignatureHtml = (
    theme: 'light' | 'dark',
    fullName: string,
    jobTitle: string,
    email: string
) => {
    const t = PANEL_THEMES[theme]
    const { name, title, mail, mailHref } = personFields(fullName, jobTitle, email)

    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:separate;border-spacing:0;mso-table-lspace:0pt;mso-table-rspace:0pt;width:100%;font-family:Helvetica,Arial,sans-serif;">
  <tr>
    <td height="4" bgcolor="#c78e5f" style="height:4px;background-color:#c78e5f;font-size:0;line-height:0;">&nbsp;</td>
  </tr>
  <tr>
    <td bgcolor="${t.panelBg}" style="background-color:${t.panelBg};border-left:1px solid ${t.panelBorder};border-right:1px solid ${t.panelBorder};padding:0 28px 18px 28px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
        <tr>
          <td height="18" style="height:18px;font-size:0;line-height:0;">&nbsp;</td>
        </tr>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
        <tr>
          <td valign="middle" style="vertical-align:middle;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              <tr>
                <td colspan="2" style="font-family:Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;color:${t.nameColor};letter-spacing:-0.2px;line-height:22px;mso-line-height-rule:exactly;">
                  ${name}
                </td>
              </tr>
              <tr>
                <td colspan="2" style="font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:400;color:${t.titleColor};letter-spacing:0.2px;line-height:16px;mso-line-height-rule:exactly;padding:2px 0 14px 0;">
                  ${title}
                </td>
              </tr>
              <tr>
                <td width="8" valign="baseline" style="width:8px;padding-right:8px;white-space:nowrap;vertical-align:baseline;font-family:Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;color:${t.labelColor};line-height:18px;mso-line-height-rule:exactly;">E</td>
                <td valign="baseline" style="vertical-align:baseline;font-family:Helvetica,Arial,sans-serif;font-size:13px;color:${t.bodyColor};line-height:18px;mso-line-height-rule:exactly;white-space:nowrap;">
                  <a href="mailto:${mailHref}" style="color:${t.bodyColor};text-decoration:none;"><span style="color:${t.bodyColor};">${mail}</span></a>
                </td>
              </tr>
              <tr>
                <td width="8" valign="baseline" style="width:8px;padding-right:8px;white-space:nowrap;vertical-align:baseline;font-family:Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;color:${t.labelColor};line-height:18px;mso-line-height-rule:exactly;">W</td>
                <td valign="baseline" style="vertical-align:baseline;font-family:Helvetica,Arial,sans-serif;font-size:13px;color:${t.bodyColor};line-height:18px;mso-line-height-rule:exactly;white-space:nowrap;">
                  <a href="${WEBSITE_HREF}" style="color:${t.bodyColor};text-decoration:none;"><span style="color:${t.bodyColor};">${WEBSITE_DISPLAY}</span></a>
                </td>
              </tr>
            </table>
          </td>
          <td width="20" style="width:20px;font-size:0;line-height:0;">&nbsp;</td>
          <td valign="middle" align="right" style="vertical-align:middle;">
            <table role="presentation" align="right" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              <tr>
                <td align="center" style="font-family:'Arial Black','Helvetica Neue',Helvetica,Arial,sans-serif;font-size:26px;font-weight:900;color:${t.wordmarkColor};letter-spacing:-1px;line-height:26px;mso-line-height-rule:exactly;text-align:center;white-space:nowrap;">
                  Sentinel
                </td>
              </tr>
              <tr>
                <td align="center" style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:10px;font-weight:500;color:${t.wordmarkColor};letter-spacing:4.5px;line-height:14px;mso-line-height-rule:exactly;padding:2px 0 0 4.5px;text-align:center;white-space:nowrap;">
                  LEGAL
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td bgcolor="${t.footerBg}" style="background-color:${t.footerBg};border-left:1px solid ${t.panelBorder};border-right:1px solid ${t.panelBorder};padding:10px 28px 0 28px;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:${t.footerText};line-height:16px;mso-line-height-rule:exactly;">
      ${ADDRESS_LINE}<span style="color:#c78e5f;padding:0 4px;"> &bull; </span>SRA No. ${SRA_NO}
    </td>
  </tr>
  <tr>
    <td bgcolor="${t.footerBg}" style="background-color:${t.footerBg};border-left:1px solid ${t.panelBorder};border-right:1px solid ${t.panelBorder};border-bottom:1px solid ${t.panelBorder};border-radius:0 0 10px 10px;padding:2px 28px 10px 28px;font-family:Helvetica,Arial,sans-serif;font-size:9px;color:${t.noticeText};line-height:13px;mso-line-height-rule:exactly;">
      This email and any attachments are confidential and intended solely for the addressee.
      If you have received this email in error, please notify the sender and delete it.
      Sentinel Legal is authorised and regulated by the Solicitors Regulation Authority.
    </td>
  </tr>
</table>`
}

/**
 * Email-client-friendly signature markup (table + inline styles).
 * Light/dark match the panel templates; minimal is text-first with a copper accent bar.
 */
export const buildStaffEmailSignatureHtml = ({
    fullName,
    jobTitle,
    email,
    theme = 'light',
}: StaffEmailSignatureFields) => {
    if (theme === 'minimal') return buildMinimalSignatureHtml(fullName, jobTitle, email)
    return buildPanelSignatureHtml(theme, fullName, jobTitle, email)
}
